import { HandleableError } from "@context-providers/error-handler/error-handler-types";
import * as Sentry from "@sentry/react";
import { type Event } from "@sentry/react";
import { EmailUtils } from "@stellar/utils";

/**
 * Sentry tag marking events from internal FARO automation/test accounts.
 * Set as a string ("true"/"false") because `beforeSend` receives the raw tag value,
 * so the drop check compares against the string.
 */
export const AUTOMATION_TEST_USER_TAG = "isAutomationTestUser";

/**
 * Whether a Sentry event originates from an internal automation/test account and
 * should therefore be dropped in `beforeSend` to avoid polluting the logs.
 */
export function isAutomationTestEvent(event: Event): boolean {
  return event.tags?.[AUTOMATION_TEST_USER_TAG] === "true";
}

/**
 * Set the global Sentry automation/test tag from the given email so those events can be
 * dropped in `beforeSend`. Derived from a concrete email (typed input or authenticated
 * user) so it stays correct across all login/redirect flows.
 */
export function setAutomationTestUserTag(email: string): void {
  Sentry.setTag(
    AUTOMATION_TEST_USER_TAG,
    String(EmailUtils.isAutomationTestUser(email))
  );
}

interface Props {
  /** The actual error object */
  error: HandleableError;

  /** The error title */
  title?: string;

  /** Additional data to be attached to error */
  data?: Record<string, unknown>;

  /** Whether the error is an assertion error currently thrown by Zod */
  isAssertionError?: boolean;
}

/** Avoid "Object captured as exception with keys: message, name, stack" without any info. */
export class ErrorFromJson extends Error {
  constructor(obj: { message: string; name?: string; stack?: string }) {
    super(obj.message);
    try {
      if (typeof obj.name === "string") {
        this.name = obj.name;
      }
      if (typeof obj.stack === "string") {
        this.stack = obj.stack;
      }
    } catch {
      // Catch errors, just in case that some browsers disallow setting e.g. the stack.
    }
  }
}

/**
 * Capturing Errors and Events in Sentry.
 * A simple text should be passed as message, additional data should be passed as data
 * https://github.com/getsentry/sentry-javascript/issues/1607#issuecomment-825887112
 * https://github.com/getsentry/sentry-react-native/issues/1033#issuecomment-2143619805
 */
export function sentryCaptureError({
  error,
  title,
  data,
  isAssertionError = false,
}: Props): void {
  let errorObject: Error | unknown = error;
  if (typeof error === "string") {
    // Avoid <unknown> as the issue name.
    errorObject = new Error(error);
  } else if (
    !(error instanceof Error) &&
    typeof (error as Error)?.message === "string"
  ) {
    // Avoid "Object captured as exception with keys: message, name, stack" without any info.
    errorObject = new ErrorFromJson(error as Error);
  }

  // Mark the assertion error with "AssertionError" title for easier filtering in Sentry
  if (isAssertionError) {
    (errorObject as Error).name = "AssertionError";
  }

  Sentry.captureException(errorObject, (scope) => {
    if (title) {
      // Set transaction name instead of the default current uri, which is useless
      // this way it's easier see the specific problem in the list and filter specific problems/errors
      scope.setTransactionName(title);
      scope.addBreadcrumb({
        type: "error",
        category: "error",
        level: "error",
        message: title,
      });
    }
    if (data) {
      scope.setContext("extra-data", data);
    }
    return scope;
  });
}

/**
 * Capturing a warning in Sentry, for handled cases that are still worth counting.
 *
 * @param message describing the case, which Sentry groups the warnings by
 * @param data additional data to be attached to the warning
 */
export function sentryCaptureWarning({
  message,
  data,
}: {
  message: string;
  data?: Record<string, unknown>;
}): void {
  Sentry.captureMessage(message, (scope) => {
    scope.setLevel("warning");
    if (data) {
      scope.setContext("extra-data", data);
    }
    return scope;
  });
}
